"use client";

import { c as _c } from "react/compiler-runtime";
import { useT, useGameLocale } from "@/lib/i18n";
import { useState, useEffect, useRef } from "react";
import { Link } from "@/i18n/navigation";
import { usePathname } from "next/navigation";
import LanguageSelector from "./LanguageSelector";
import SearchTrigger from "./SearchTrigger";
import SiteSwitcher from "./SiteSwitcher";
import LiveNavButton from "./LiveNavButton";
import AnnouncementBadge, { useAnnouncementUnread } from "./AnnouncementBadge";
import { useAuth } from "@/app/contexts/AuthContext";
import DiscordIcon from "./DiscordIcon";
import ThemeToggle from "./ThemeToggle";
import Wordmark from "./Wordmark";
import { recordRecent, getRecent, isRecentType, ENTITY_SINGULAR, prettyRecentName, rememberRecentName, fetchRecentName, type RecentEntity } from "@/lib/recent-entities";
import { cachedFetch } from "@/lib/fetch-cache";
import { SITE_URL } from "@/lib/seo";
import { LANG_PREFIXES } from "@/lib/languages";
const LANG_CODES = LANG_PREFIXES;
const API_BASE = process.env.NEXT_PUBLIC_API_URL ?? "http://localhost:8000";
interface NavGroup {
  label: string;
  links: {
    href: string;
    label: string;
    badge?: "discord-bot";
  }[];
}

// Routes that should only highlight on exact match (not prefix match)
const EXACT_MATCH = new Set(["/leaderboards", "/stats", "/runs"]);
function isLinkActive(strippedPath: string, href: string): boolean {
  if (EXACT_MATCH.has(href)) return strippedPath === href;
  // Match on path-segment boundaries so e.g. "/tier-list" doesn't light up
  // when the active route is "/tier-list-maker".
  return strippedPath === href || strippedPath.startsWith(`${href}/`);
}

/** Renders a nav-link label, with a compact Discord-icon + "bot" tag after
 * it for `badge: "discord-bot"` links (shorter than spelling out "(Discord
 * Bot)", which clipped the menu). */
function NavLinkLabel(t0) {
  const $ = _c(16);
  const {
    label,
    badge
  } = t0;
  const t = useT();
  if (badge === "discord-bot") {
    let t1;
    if ($[0] !== label || $[1] !== t) {
      t1 = t(label);
      $[0] = label;
      $[1] = t;
      $[2] = t1;
    } else {
      t1 = $[2];
    }
    let t2;
    if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
      t2 = <DiscordIcon className="w-3.5 h-3.5 shrink-0" />;
      $[3] = t2;
    } else {
      t2 = $[3];
    }
    let t3;
    if ($[4] !== t) {
      t3 = t("bot");
      $[4] = t;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    let t4;
    if ($[6] !== t3) {
      t4 = <span className="inline-flex items-center gap-0.5 text-[var(--text-muted)]">{t2}<span className="text-xs">{t3}</span></span>;
      $[6] = t3;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    let t5;
    if ($[8] !== t1 || $[9] !== t4) {
      t5 = <span className="inline-flex items-center gap-1.5">{t1}{t4}</span>;
      $[8] = t1;
      $[9] = t4;
      $[10] = t5;
    } else {
      t5 = $[10];
    }
    return t5;
  }
  let t1;
  if ($[11] !== label || $[12] !== t) {
    t1 = t(label);
    $[11] = label;
    $[12] = t;
    $[13] = t1;
  } else {
    t1 = $[13];
  }
  let t2;
  if ($[14] !== t1) {
    t2 = <>{t1}</>;
    $[14] = t1;
    $[15] = t2;
  } else {
    t2 = $[15];
  }
  return t2;
}
const NAV_GROUPS: NavGroup[] = [{
  label: "Compendium",
  links: [{
    href: "/cards",
    label: "Card Library"
  }, {
    href: "/relics",
    label: "Relic Collection"
  }, {
    href: "/potions",
    label: "Potion Lab"
  }, {
    href: "/enchantments",
    label: "Enchantments"
  }, {
    href: "/powers",
    label: "Powers"
  }, {
    href: "/keywords",
    label: "Keywords"
  }, {
    href: "/characters",
    label: "Characters"
  }, {
    href: "/monsters",
    label: "Bestiary"
  }, {
    href: "/encounters",
    label: "Encounters"
  }, {
    href: "/events",
    label: "Events"
  }, {
    href: "/ancients",
    label: "Ancients"
  }, {
    href: "/merchant",
    label: "Merchant"
  }, {
    href: "/modifiers",
    label: "Custom Mode"
  }, {
    href: "/mechanics",
    label: "Mechanics"
  }, {
    href: "/unlocks",
    label: "Unlockables"
  }, {
    href: "/timeline",
    label: "Timeline"
  }, {
    href: "/images",
    label: "Images"
  }, {
    href: "/reference",
    label: "Reference"
  }, {
    href: "/badges",
    label: "Badges"
  }, {
    href: "/compare",
    label: "Compare Characters"
  }, {
    href: "/guides",
    label: "Guides"
  }]
}, {
  label: "Stats",
  links: [{
    href: "/stats",
    label: "Overview"
  }, {
    href: "/stats/cards",
    label: "Cards"
  }, {
    href: "/stats/relics",
    label: "Relics"
  }, {
    href: "/stats/potions",
    label: "Potions"
  }, {
    href: "/stats/encounters",
    label: "Encounters"
  }, {
    href: "/stats/charts",
    label: "Charts"
  }, {
    href: "/stats/chart-builder",
    label: "Chart builder"
  }, {
    href: "/stats/shops",
    label: "Shops"
  }, {
    href: "/stats/events",
    label: "Events"
  }, {
    href: "/stats/campfires",
    label: "Campfires"
  }, {
    href: "/leaderboards",
    label: "Leaderboards"
  }, {
    href: "/top-players",
    label: "Top Players"
  }, {
    href: "/tier-list",
    label: "Tier List"
  }, {
    href: "/stats/scoring",
    label: "Scoring"
  }, {
    href: "/runs",
    label: "Browse Runs"
  }, {
    href: "/replays",
    label: "Browse Replays"
  }, {
    href: "/runs/submit",
    label: "Contribute"
  }]
}, {
  label: "Tools",
  links: [{
    href: "/tier-list-maker",
    label: "Tier List Maker"
  }, {
    href: "/seed-finder",
    label: "Seed Finder"
  }, {
    href: "/deck-builder",
    label: "Deck Builder"
  }, {
    href: "/mod",
    label: "Steam Mod"
  }, {
    href: "/exporter",
    label: "Art Exporter"
  }, {
    href: "/overlay",
    label: "Overlay (Overwolf)"
  }, {
    href: "/showcase",
    label: "Showcase"
  }, {
    href: "/knowledge-demon",
    label: "Knowledge Demon",
    badge: "discord-bot"
  }, {
    href: "/developers",
    label: "Developers"
  },
  // Absolute URL on purpose: bare "/docs" reads as an internal route and
  // gets the language prefix, but the FastAPI docs only exist at the
  // root (every /<lang>/docs was a crawled 404).
  {
    href: `${API_BASE || SITE_URL}/docs`,
    label: "API"
  }]
}, {
  label: "About",
  links: [{
    href: "/about",
    label: "Spire Codex"
  }, {
    href: "/changelog",
    label: "Changelog"
  }, {
    href: "/news",
    label: "News"
  }, {
    href: "/thank-you",
    label: "Thank You"
  }, {
    href: "https://www.patreon.com/cw/SpireCodex",
    label: "Patreon"
  }, {
    href: "https://ko-fi.com/spirecodex",
    label: "Ko-fi"
  }, {
    href: "/privacy",
    label: "Privacy"
  }, {
    href: "/terms",
    label: "Terms"
  }, {
    href: "https://discord.gg/xMsTBeh",
    label: "Discord"
  }, {
    href: "https://github.com/ptrlrd/spire-codex",
    label: "GitHub"
  }, {
    href: "#feedback",
    label: "Feedback"
  }, {
    href: "mailto:media@spire-codex.com",
    label: "Email"
  }]
}];

// Color + live-count metadata so the Database mega reads like a compendium
// index (color chip + count) instead of a plain link list, matching the redesign.
const DB_META: Record<string, {
  color: string;
  count?: string;
}> = {
  "/cards": {
    color: "var(--accent-gold)",
    count: "cards"
  },
  "/relics": {
    color: "var(--color-regent)",
    count: "relics"
  },
  "/potions": {
    color: "var(--color-defect)",
    count: "potions"
  },
  "/enchantments": {
    color: "var(--color-ancient)",
    count: "enchantments"
  },
  "/powers": {
    color: "var(--color-necrobinder)",
    count: "powers"
  },
  "/keywords": {
    color: "var(--color-silent)",
    count: "keywords"
  },
  "/characters": {
    color: "var(--color-ironclad)",
    count: "characters"
  },
  "/monsters": {
    color: "var(--color-ironclad)",
    count: "monsters"
  },
  "/encounters": {
    color: "var(--color-defect)",
    count: "encounters"
  },
  "/events": {
    color: "var(--color-silent)",
    count: "events"
  },
  "/ancients": {
    color: "var(--color-ancient)"
  },
  "/merchant": {
    color: "var(--color-merchant)"
  },
  "/modifiers": {
    color: "var(--color-ancient)",
    count: "modifiers"
  },
  "/mechanics": {
    color: "#596068"
  },
  "/unlocks": {
    color: "var(--color-merchant)"
  },
  "/timeline": {
    color: "#8a6b3a",
    count: "epochs"
  },
  "/images": {
    color: "var(--color-regent)",
    count: "images"
  },
  "/reference": {
    color: "#596068"
  },
  "/badges": {
    color: "var(--color-merchant)",
    count: "badges"
  },
  "/compare": {
    color: "var(--color-defect)"
  },
  "/guides": {
    color: "var(--color-silent)"
  }
};

// Each nav group opens a multi-column mega panel. Columns reference links by
// their (English) label so beta-hidden links drop out automatically.
const NAV_COLUMNS: Record<string, {
  title: string;
  labels: string[];
}[]> = {
  Compendium: [{
    title: "Cards & Combat",
    labels: ["Card Library", "Relic Collection", "Potion Lab", "Enchantments", "Powers", "Keywords"]
  }, {
    title: "The Run",
    labels: ["Characters", "Bestiary", "Encounters", "Events", "Ancients", "Merchant"]
  }, {
    title: "Systems & Meta",
    labels: ["Custom Mode", "Mechanics", "Unlockables"]
  }, {
    title: "Reference",
    labels: ["Timeline", "Images", "Reference", "Badges", "Compare Characters", "Guides"]
  }],
  Stats: [{
    title: "Stats",
    labels: ["Overview", "Cards", "Relics", "Potions", "Encounters", "Charts", "Chart builder"]
  }, {
    title: "Decisions",
    labels: ["Shops", "Events", "Campfires"]
  }, {
    title: "Rankings",
    labels: ["Leaderboards", "Top Players", "Tier List", "Scoring"]
  }, {
    title: "Runs",
    labels: ["Browse Runs", "Browse Replays", "Contribute"]
  }],
  Tools: [{
    title: "Make & share",
    labels: ["Tier List Maker", "Seed Finder", "Deck Builder", "Showcase"]
  }, {
    title: "Companion apps",
    labels: ["Steam Mod", "Art Exporter", "Overlay (Overwolf)", "Knowledge Demon"]
  }, {
    title: "Build with the data",
    labels: ["Developers", "API"]
  }],
  About: [{
    title: "Project",
    labels: ["Spire Codex", "Changelog", "News", "Thank You"]
  }, {
    title: "Support",
    labels: ["Patreon", "Ko-fi", "Feedback", "Email"]
  }, {
    title: "Elsewhere",
    labels: ["Discord", "GitHub", "Privacy", "Terms"]
  }]
};
export default function Navbar() {
  const pathname = usePathname();
  const lang = useGameLocale();
  const t = useT();
  const pathLang = pathname.split("/")[1];
  const currentLang = LANG_CODES.has(pathLang) ? pathLang : null;
  const strippedPath = currentLang ? pathname.replace(`/${currentLang}`, "") || "/" : pathname;
  const isHome = strippedPath === "/";
  const {
    user,
    loading: authLoading,
    loginSteam,
    logout
  } = useAuth();
  const [open, setOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const {
    unread: newsUnread
  } = useAnnouncementUnread();
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const userButtonRef = useRef<HTMLButtonElement>(null);

  // Close menus when clicking outside
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (open && menuRef.current && !menuRef.current.contains(e.target as Node) && buttonRef.current && !buttonRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
      if (userMenuOpen && userMenuRef.current && !userMenuRef.current.contains(e.target as Node) && userButtonRef.current && !userButtonRef.current.contains(e.target as Node)) {
        setUserMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open, userMenuOpen]);

  // Close menus on route change
  useEffect(() => {
    setOpen(false);
    setUserMenuOpen(false);
    // Clear nav focus after navigation so focus-within dropdowns close
    // (keyboard support preserved). preventScroll matters: without it the
    // browser scrolls <main> into view, which on small screens jumps past
    // the donation banner that sits above main in the layout.
    const main = document.querySelector("main");
    if (main instanceof HTMLElement) {
      main.focus({
        preventScroll: true
      });
    }
  }, [pathname]);
  function toggleGroup(label: string) {
    setExpandedGroups(prev => {
      if (prev.has(label)) return new Set();
      return new Set([label]);
    });
  }
  const [navStats, setNavStats] = useState<Record<string, number> | null>(null);
  useEffect(() => {
    cachedFetch<Record<string, number>>(`${API_BASE}/api/stats?lang=${lang}`).then(d => setNavStats(d ?? null)).catch(() => {});
  }, [lang]);
  const [recents, setRecents] = useState<RecentEntity[]>([]);
  useEffect(() => {
    const parts = strippedPath.split("/").filter(Boolean);
    if (parts.length === 2 && isRecentType(parts[0])) recordRecent(parts[0], parts[1]);
    setRecents(getRecent());
  }, [strippedPath]);
  const [recentNames, setRecentNames] = useState<Record<string, string>>({});
  useEffect(() => {
    let cancelled = false;
    for (const r of recents.slice(0, 3)) {
      const key = `${lang}:${r.type}:${r.id}`;
      if (r.names?.[lang] || recentNames[key]) continue;
      fetchRecentName(r.type, r.id, lang).then(name => {
        if (!name || cancelled) return;
        rememberRecentName(r.type, r.id, lang, name);
        setRecentNames(m => {
          const next = {
            ...m,
            [key]: name
          };
          const keys = Object.keys(next);
          // The store holds 12 entities; keep a small multiple so a few
          // language switches stay cached without growing without bound.
          if (keys.length > 48) for (const k of keys.slice(0, keys.length - 48)) delete next[k];
          return next;
        });
      });
    }
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recents, lang]);
  const renderMega = (group: NavGroup, isLast: boolean) => {
    const links = group.links;
    if (links.length === 0) return null;
    const hasActive = links.some(link => !link.href.startsWith("http") && isLinkActive(strippedPath, link.href));
    const linkByLabel = new Map(links.map(l => [l.label, l]));
    const cols = (NAV_COLUMNS[group.label] ?? [{
      title: "",
      labels: links.map(l => l.label)
    }]).map(c => ({
      title: c.title,
      links: c.labels.map(lbl => linkByLabel.get(lbl)).filter(Boolean) as {
        href: string;
        label: string;
        badge?: "discord-bot";
      }[]
    })).filter(c => c.links.length > 0);
    return <div key={group.label} className="relative group">
        <button type="button" aria-haspopup="menu" onMouseDown={e => e.preventDefault()} className={`flex items-center gap-1.5 px-3.5 py-2 text-sm font-medium rounded-md transition-colors ${hasActive ? "text-[var(--accent-gold)]" : "text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-card)]"} group-hover:bg-[var(--bg-card)] group-focus-within:bg-[var(--bg-card)]`}>
          {t(group.label)}
          <svg aria-hidden viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4 text-[var(--text-secondary)] transition-transform group-hover:rotate-180 group-focus-within:rotate-180">
            <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.168l3.71-3.938a.75.75 0 1 1 1.08 1.04l-4.25 4.5a.75.75 0 0 1-1.08 0l-4.25-4.5a.75.75 0 0 1 .02-1.06z" clipRule="evenodd" />
          </svg>
        </button>
        <div className={`absolute ${isLast ? "right-0" : "left-0"} top-full pt-2 hidden group-hover:block group-focus-within:block`}>
          <div role="menu" className="flex gap-6 rounded-xl border border-[var(--border-subtle)] bg-[var(--bg-secondary)] shadow-2xl shadow-scrim/40 p-4">
            {cols.map((col, ci) => <div key={ci} className="min-w-[9.5rem]">
                {col.title && <div className="mb-1.5 border-b border-[var(--border-subtle)] px-2.5 pb-1.5 font-mono text-[10px] font-medium uppercase tracking-[0.09em] text-[var(--color-silent)]">
                    {t(col.title)}
                  </div>}
                <div className="flex flex-col gap-0.5">
                  {col.links.map(link => {
                const isInternal = link.href.startsWith("/");
                const isHttp = link.href.startsWith("http");
                const fullHref = link.href;
                const isActive = isInternal && isLinkActive(strippedPath, link.href);
                const meta = DB_META[link.href];
                const countVal = meta?.count ? navStats?.[meta.count] : undefined;
                const className = `flex items-center gap-2.5 rounded-md px-2.5 py-1.5 text-sm font-medium whitespace-nowrap transition-colors ${isActive ? "text-[var(--accent-gold)] bg-[var(--bg-card)]" : "text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--bg-card)]"}`;
                const inner = <>
                        {meta && <span className="h-2.5 w-2.5 shrink-0 rounded-[3px]" style={{
                    background: meta.color
                  }} aria-hidden />}
                        <span className="flex-1">
                          <NavLinkLabel label={link.label} badge={link.badge} lang={lang} />
                        </span>
                        {countVal != null && <span className="font-mono text-xs tabular-nums text-[var(--text-muted)]">
                            {countVal}
                          </span>}
                      </>;
                return isInternal ? <Link prefetch={false} key={link.href} href={fullHref} role="menuitem" className={className} onMouseDown={e => e.preventDefault()}>
                        {inner}
                      </Link> : <a key={link.href} href={fullHref} {...isHttp ? {
                  target: "_blank",
                  rel: "noopener noreferrer"
                } : {}} role="menuitem" onMouseDown={e => e.preventDefault()} className={className}>
                        {inner}
                      </a>;
              })}
                </div>
              </div>)}
            {group.label === "Compendium" && <div className="min-w-[12rem] border-l border-[var(--border-subtle)] pl-6">
                <div className="mb-1.5 border-b border-[var(--border-subtle)] px-2.5 pb-1.5 font-mono text-[10px] font-medium uppercase tracking-[0.09em] text-[var(--color-silent)]">
                  {t("Jump back in")}
                </div>
                <div className="flex flex-col gap-0.5">
                  {recents.slice(0, 3).map(r => {
                const rmeta = DB_META["/" + r.type];
                return <Link prefetch={false} key={`${r.type}-${r.id}`} href={`/${r.type}/${r.id}`} role="menuitem" onMouseDown={e => e.preventDefault()} className="flex items-center gap-2.5 rounded-md px-2.5 py-1.5 hover:bg-[var(--bg-card)] transition-colors">
                        <span className="h-2.5 w-2.5 shrink-0 rounded-[3px]" style={{
                    background: rmeta?.color ?? "var(--text-muted)"
                  }} aria-hidden />
                        <span className="flex min-w-0 flex-col">
                          <span className="truncate text-sm font-medium text-[var(--text-primary)]">
                            {r.names?.[lang] ?? recentNames[`${lang}:${r.type}:${r.id}`] ?? prettyRecentName(r.id)}
                          </span>
                          <span className="text-xs text-[var(--text-muted)]">
                            {t(ENTITY_SINGULAR[r.type] ?? r.type)}
                          </span>
                        </span>
                      </Link>;
              })}
                  {recents.length === 0 && <p className="px-2.5 py-1.5 text-xs text-[var(--text-muted)]">
                      {t("Pages you open show up here.")}
                    </p>}
                </div>
              </div>}
          </div>
        </div>
      </div>;
  };
  return <nav className="fixed top-0 left-0 right-0 z-50 border-b border-[var(--border-subtle)] bg-[var(--bg-primary)]/95 backdrop-blur-sm">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between gap-3 sm:gap-4 h-16">
          {/* Left: logo + nav pushed tight together; the cluster is pushed right */}
          <div className="flex items-center gap-2 sm:gap-6 min-w-0">
            <Link prefetch={false} href="/" className="flex items-center gap-2 shrink-0">
              <Wordmark />
            </Link>
            {/* Desktop nav (lg+): groups + Live, tight against the logo */}
            <div className="hidden lg:flex items-center gap-1">
              {NAV_GROUPS.map((group, i) => renderMega(group, i === NAV_GROUPS.length - 1))}
              <LiveNavButton />
              <AnnouncementBadge />
            </div>
          </div>

          {/* Inline search bar, md only. At lg+ the nav owns the row
              and search collapses to the icon trigger in the right cluster
              (Apple / Linear pattern). The icon still opens the same modal
              and the `.` global hotkey works at every breakpoint. */}
          {!isHome && <div className="hidden md:flex lg:hidden flex-1 max-w-md">
              <SearchTrigger variant="nav" />
            </div>}

          <div className="flex items-center gap-2 shrink-0">
            <div className="hidden sm:flex items-center gap-2">
              <SiteSwitcher />
              <LanguageSelector />
            </div>

            {/* Icon search, visible on mobile (below md) AND at lg+
                where the inline bar collapses. Sits next to the language
                selector in the right cluster. */}
            <div className="md:hidden lg:flex">
              <SearchTrigger variant="icon" />
            </div>

            {!authLoading && <div className="relative">
                <button ref={userButtonRef} onClick={() => {
              if (user) {
                setUserMenuOpen(!userMenuOpen);
              }
            }} className="inline-flex items-center justify-center h-9 min-w-[2.25rem] px-1.5 sm:px-2 rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-card)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--border-accent)] transition-colors gap-1.5" aria-label={user ? t("Account menu") : t("Sign in")}>
                  <svg className="w-4 h-4 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  {user ? <span className="hidden sm:inline text-xs font-medium truncate max-w-[80px]">
                      {user.username || t("Account")}
                    </span> : null}
                </button>

                {/* Signed-out: login options */}
                {!user && !userMenuOpen && <div className="absolute right-0 top-full mt-2 w-44 max-w-[calc(100vw-1rem)] rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-primary)] shadow-xl shadow-scrim/30 p-1.5 hidden" id="login-options" />}

                {!user && <button onClick={() => setUserMenuOpen(!userMenuOpen)} className="absolute inset-0 w-full h-full opacity-0" aria-label={t("Sign in options")} tabIndex={-1} />}

                {userMenuOpen && !user && <div ref={userMenuRef} className="absolute right-0 top-full mt-2 w-44 max-w-[calc(100vw-1rem)] rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-primary)] shadow-xl shadow-scrim/30 p-1.5 z-50">
                    <p className="px-2.5 py-1.5 text-xs text-[var(--text-tertiary)] font-medium">
                      {t("Sign in with")}
                    </p>
                    <a href={loginSteam} onClick={() => setUserMenuOpen(false)} className="w-full flex items-center gap-2 px-2.5 py-2 text-sm rounded-md hover:bg-[var(--bg-card)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors">
                      <svg className="w-4 h-4 shrink-0" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M11.979 0C5.678 0 .511 4.86.022 11.037l6.432 2.658a3.387 3.387 0 0 1 1.912-.593c.064 0 .127.003.19.007l2.862-4.146v-.058a4.533 4.533 0 0 1 4.53-4.53 4.533 4.533 0 0 1 4.53 4.53 4.533 4.533 0 0 1-4.53 4.53h-.106l-4.08 2.91c0 .053.003.107.003.161a3.4 3.4 0 0 1-3.4 3.4 3.404 3.404 0 0 1-3.367-2.936L.256 15.21C1.542 20.2 6.218 24 11.979 24 18.627 24 24 18.627 24 11.979 24 5.373 18.627 0 11.979 0z" />
                      </svg>
                      Steam
                    </a>
                  </div>}

                {/* Signed-in: account dropdown */}
                {userMenuOpen && user && <div ref={userMenuRef} className="absolute right-0 top-full mt-2 w-48 max-w-[calc(100vw-1rem)] rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-primary)] shadow-xl shadow-scrim/30 p-1.5 z-50">
                    <div className="px-2.5 py-1.5 border-b border-[var(--border-subtle)] mb-1">
                      <p className="text-sm font-medium text-[var(--text-primary)] truncate">
                        {user.username || t("User")}
                      </p>
                      {user.email && <p className="text-xs text-[var(--text-tertiary)] truncate">
                          {user.email}
                        </p>}
                    </div>
                    <Link prefetch={false} href="/profile" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2 px-2.5 py-2 text-sm rounded-md hover:bg-[var(--bg-card)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors">
                      {t("Profile")}
                    </Link>
                    <Link prefetch={false} href="/settings" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2 px-2.5 py-2 text-sm rounded-md hover:bg-[var(--bg-card)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors">
                      {t("Settings")}
                    </Link>
                    {/* Admin pages are unlocalized. The
                      link is cosmetic gating only; /admin itself 404s for
                      anyone not on the server-side allowlist. */}
                    {user.is_admin && <Link prefetch={false} href="/admin" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2 px-2.5 py-2 text-sm rounded-md hover:bg-[var(--bg-card)] text-[var(--accent-gold)] hover:text-[var(--accent-gold)] transition-colors">
                        {t("Admin")}
                      </Link>}
                    <button onClick={() => {
                setUserMenuOpen(false);
                logout();
              }} className="w-full flex items-center gap-2 px-2.5 py-2 text-sm rounded-md hover:bg-[var(--bg-card)] text-danger hover:text-danger transition-colors">
                      {t("Sign Out")}
                    </button>
                  </div>}
              </div>}

            <div className="hidden lg:flex">
              <ThemeToggle />
            </div>

            {/* Burger button -- hidden at lg+ where the secondary nav row below takes over */}
            <div className="relative lg:hidden">
              <button ref={buttonRef} onClick={() => setOpen(!open)} className="relative inline-flex items-center justify-center h-9 w-9 rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-card)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--border-accent)] transition-colors" aria-label={t("Toggle menu")}>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  {open ? <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /> : <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />}
                </svg>
                {newsUnread && !open && <span className="absolute -top-1 -right-1 flex h-2.5 w-2.5" aria-hidden>
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[var(--accent-gold)] opacity-75" />
                    <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-[var(--accent-gold)]" />
                  </span>}
              </button>

              {/* Dropdown menu, `right-0` anchors to the burger's relative
                parent. Layout shifts (e.g. the SiteSwitcher button text
                growing on beta after /api/versions resolves) used to push
                the parent past the viewport edge, dragging the dropdown
                off-screen. The `max-w-[calc(100vw-1rem)]` clamps the
                dropdown width to viewport-minus-1rem so even if the
                anchor drifts, the menu stays reachable. */}
              {open && <div ref={menuRef} className="fixed top-0 left-0 z-50 h-screen w-screen flex flex-col bg-[var(--bg-primary)]">
                  <div className="flex items-center justify-between h-16 px-5 border-b border-[var(--border-subtle)] shrink-0">
                    <Wordmark />
                    <button onClick={() => setOpen(false)} aria-label={t("Close menu")} className="inline-flex items-center justify-center h-9 w-9 rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-card)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:border-[var(--border-accent)] transition-colors">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2} viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <div className="flex-1 overflow-y-auto pb-10">
                    {NAV_GROUPS.map(group => {
                  const links = group.links;
                  if (links.length === 0) return null;
                  const isExpanded = expandedGroups.has(group.label);
                  const hasActive = links.some(link => !link.href.startsWith("http") && isLinkActive(strippedPath, link.href));
                  const linkByLabel = new Map(links.map(l => [l.label, l]));
                  const cols = (NAV_COLUMNS[group.label] ?? [{
                    title: "",
                    labels: links.map(l => l.label)
                  }]).map(c => ({
                    title: c.title,
                    links: c.labels.map(lbl => linkByLabel.get(lbl)).filter(Boolean) as {
                      href: string;
                      label: string;
                      badge?: "discord-bot";
                    }[]
                  })).filter(c => c.links.length > 0);
                  return <div key={group.label} className="border-b border-[var(--border-subtle)]">
                          <button onClick={() => toggleGroup(group.label)} className={`w-full flex items-center justify-between px-5 py-4 text-lg font-semibold transition-colors ${hasActive ? "text-[var(--accent-gold)]" : "text-[var(--text-primary)]"}`}>
                            {t(group.label)}
                            <svg aria-hidden viewBox="0 0 20 20" fill="currentColor" className={`w-4 h-4 text-[var(--text-muted)] transition-transform ${isExpanded ? "rotate-180" : ""}`}>
                              <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 0 1 1.06.02L10 11.168l3.71-3.938a.75.75 0 1 1 1.08 1.04l-4.25 4.5a.75.75 0 0 1-1.08 0l-4.25-4.5a.75.75 0 0 1 .02-1.06z" clipRule="evenodd" />
                            </svg>
                          </button>
                          {isExpanded && <div className="px-5 pb-4">
                              {cols.map((col, ci) => <div key={ci}>
                                  {col.title && <div className="pt-3 pb-1 font-mono text-[10px] font-medium uppercase tracking-[0.09em] text-[var(--color-silent)]">
                                      {t(col.title)}
                                    </div>}
                                  {col.links.map(link => {
                          const isInternal = link.href.startsWith("/");
                          const isHttp = link.href.startsWith("http");
                          const fullHref = link.href;
                          const isActive = isInternal && isLinkActive(strippedPath, link.href);
                          const meta = DB_META[link.href];
                          const countVal = meta?.count ? navStats?.[meta.count] : undefined;
                          const cls = `flex items-center gap-3 py-2.5 text-[15px] transition-colors ${isActive ? "text-[var(--accent-gold)]" : "text-[var(--text-secondary)] hover:text-[var(--text-primary)]"}`;
                          const inner = <>
                                        {meta && <span className="h-2.5 w-2.5 shrink-0 rounded-[3px]" style={{
                              background: meta.color
                            }} aria-hidden />}
                                        <span className="flex-1">
                                          <NavLinkLabel label={link.label} badge={link.badge} lang={lang} />
                                        </span>
                                        {countVal != null && <span className="font-mono text-sm tabular-nums text-[var(--text-muted)]">
                                            {countVal}
                                          </span>}
                                      </>;
                          return isInternal ? <Link prefetch={false} key={link.href} href={fullHref} className={cls}>
                                        {inner}
                                      </Link> : <a key={link.href} href={fullHref} {...isHttp ? {
                            target: "_blank",
                            rel: "noopener noreferrer"
                          } : {}} className={cls}>
                                        {inner}
                                      </a>;
                        })}
                                </div>)}
                            </div>}
                        </div>;
                })}

                    <AnnouncementBadge variant="mobile" />

                    <div className="border-b border-[var(--border-subtle)] px-5 py-3">
                      <LiveNavButton variant="mobile" />
                    </div>

                    <div className="sm:hidden flex items-center gap-2 border-b border-[var(--border-subtle)] px-5 py-3">
                      <SiteSwitcher align="left" />
                      <LanguageSelector align="left" />
                    </div>

                    <ThemeToggle variant="segmented" />
                  </div>
                </div>}
            </div>
          </div>
        </div>
      </div>
    </nav>;
}